
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChevronDown, ChevronUp, Wand2 } from 'lucide-react';
import { useBiographyQuality } from '@/hooks/useBiographyQuality';

interface Props {
  biography: string;
  categoryId?: string;
  onBiographyChange?: (optimizedBiography: string) => void;
}

const BiographyQualityPanel: React.FC<Props> = ({ biography, categoryId = 'biography', onBiographyChange }) => {
  const { qualityScore, recommendations, seedAnchors, optimizedBiography, optimizing, lastSavedAt, optimizeAndSave } = useBiographyQuality(biography, categoryId, 'personalized');
  const [showPreview, setShowPreview] = useState(false);

  // When optimizedBiography changes and we have a callback, update the textarea
  React.useEffect(() => {
    if (optimizedBiography && onBiographyChange) {
      onBiographyChange(optimizedBiography);
    }
  }, [optimizedBiography, onBiographyChange]);

  return (
    <Card className="border border-border/30 bg-card shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-light text-foreground">Personalization Quality</CardTitle>
          <Badge variant="secondary" className="bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700">Score {qualityScore}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="space-y-2">
          <Progress value={Math.max(0, Math.min(100, qualityScore))} />
          {recommendations.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-muted-foreground">
              {recommendations.slice(0, 4).map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">Seed anchors</div>
          <div className="flex flex-wrap gap-2">
            {seedAnchors.map((a, i) => (
              <Badge key={i} variant="outline">{a.replace(/^[^:]+:/, '')}</Badge>
            ))}
            {seedAnchors.length === 0 && <div className="text-sm text-muted-foreground">No anchors detected yet</div>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={optimizeAndSave} disabled={optimizing} className="flex-1 rounded-xl bg-violet-500 hover:bg-violet-600 text-white">
            <Wand2 className="h-4 w-4 mr-2" /> {optimizing ? 'Optimizing...' : 'Optimize & Save'}
          </Button>
          <Button variant="ghost" onClick={() => setShowPreview(v => !v)} aria-expanded={showPreview} aria-controls="optimized-preview" className="rounded-xl hover:bg-accent/50">
            {showPreview ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {lastSavedAt && (
          <div className="text-xs text-muted-foreground">Last saved: {new Date(lastSavedAt).toLocaleString()}</div>
        )}

        {showPreview && optimizedBiography && (
          <div id="optimized-preview" className="rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
            {optimizedBiography}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BiographyQualityPanel;
